import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DashboardLicensesData } from "../../../network/api/DashboardLicensesData";


export default function SalesChart({ licenseStats, setIsLoading = null, setLicenseStats, setErrorMessage = null, userId = null, globalAppStats = null }) {
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState('day');


  useEffect(() => {
    if (setIsLoading) setIsLoading(true);
    if (setErrorMessage) setErrorMessage(null);
    const fetchLicenses = async () => {
      try {
        const response = await DashboardLicensesData({ owner: userId, globalAppStats: globalAppStats });
        if (response.status === "success") {
          setLicenseStats(response?.data || null);
        } else {
          if (setErrorMessage) setErrorMessage({ status: response.status, message: response.message });
        }

      } catch (error) {
        if (setErrorMessage) setErrorMessage({ status: 400, message: "Failed to fetch license data" });
      } finally {
        if (setIsLoading) setIsLoading(false);
      }
    };

    fetchLicenses();
  }, [userId, setErrorMessage, setIsLoading, setLicenseStats, globalAppStats]);


  useEffect(() => {
    if (!licenseStats) return;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const formatDateLabel = (label) => {
      const date = new Date(label);
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return label;
    };

    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const formatMonthLabel = (label) => {
      const [year, month] = label.split('-').map(Number);
      if (year === currentYear && month === currentMonth + 1) return 'This Month';
      if (year === currentYear && month === currentMonth) return 'Last Month';
      return label;
    };

    const formatYearLabel = (label) => {
      if (Number(label) === currentYear) return 'This Year';
      if (Number(label) === currentYear - 1) return 'Last Year';
      return String(label);
    };

    if (activeTab === 'day' && licenseStats.days) {
      const formatted = licenseStats.days.map(({ label, value }) => ({
        name: formatDateLabel(label),
        value
      }));
      setData(formatted);
      setTabTotal(formatted.reduce((sum, item) => sum + item.value, 0))
    } else if (activeTab === 'month' && licenseStats.months) {
      const formatted = licenseStats.months.map(({ label, value }) => ({
        name: formatMonthLabel(label),
        value
      }));
      setData(formatted);
      setTabTotal(formatted.reduce((sum, item) => sum + item.value, 0))
    } else if (activeTab === 'year' && licenseStats.years) {
      const formatted = licenseStats.years.map(({ label, value }) => ({
        name: formatYearLabel(label),
        value
      }));
      setData(formatted);
      setTabTotal(formatted.reduce((sum, item) => sum + item.value, 0))
    } else if (activeTab === 'hour' && licenseStats.hours) {
      const hourMap = {};
      licenseStats.hours.forEach(({ label, value }) => {
        hourMap[label] = value;
      });

      const fullDay = Array.from({ length: 24 }, (_, hour) => {
        const label = hour.toString().padStart(2, '0') + ':00';
        const period = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        return {
          name: `${hour12}${period}`,
          value: hourMap[label] || 0
        };
      });
      setData(fullDay);
      setTabTotal(fullDay.reduce((sum, item) => sum + item.value, 0))
    } else {
      setData([]);
    }
  }, [licenseStats, activeTab, globalAppStats]);

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim();
  const [tabTotal, setTabTotal] = useState(0);
  return (
    <div className="card">
      <div className="card-header border-0 pb-0 d-sm-flex d-block">
        <div className="">
          <h4 className="card-title text-uppercase fw-normal">
            {(activeTab === 'hour' && "Today So Far")}
            {(activeTab === 'month' && "Sales by Month")}
            {(activeTab === 'year' && "Lifetime Sales by Year")}
            {(activeTab === 'day' && "This Month So Far")}
          </h4>
          <h2 className="fw-normal text-primary">
            <span><i className="fa fa-caret-up"></i></span>
            <span>{tabTotal}</span>
          </h2>
        </div>
        <div className="card-action mb-sm-0 my-2">
          <ul className="nav nav-tabs" id="myTab" role="tablist">
            <li className="nav-item" role="presentation">
              <a href="/" onClick={(e) => { e.preventDefault(); setActiveTab('hour'); }} className={`nav-link ${activeTab === 'hour' ? 'active' : ''}`} id="hour-tab" role="tab" aria-selected={activeTab === 'hour'}>Hour</a>
            </li>
            <li className="nav-item" role="presentation">
              <a href="/" onClick={(e) => { e.preventDefault(); setActiveTab('day'); }} className={`nav-link ${activeTab === 'day' ? 'active' : ''}`} id="home-tab" data-bs-toggle="tab" data-bs-target="#home-tab-pane" role="tab" aria-selected={activeTab === 'day'}>Day</a>
            </li>
            <li className="nav-item" role="presentation">
              <a href="/" onClick={(e) => { e.preventDefault(); setActiveTab('month'); }} className={`nav-link ${activeTab === 'month' ? 'active' : ''}`} id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile-tab-pane" role="tab" aria-selected={activeTab === 'month'} tabIndex="-1">Month</a>
            </li>
            <li className="nav-item" role="presentation">
              <a href="/" onClick={(e) => { e.preventDefault(); setActiveTab('year'); }} className={`nav-link ${activeTab === 'year' ? 'active' : ''}`} id="contact-tab" data-bs-toggle="tab" data-bs-target="#contact-tab-pane" role="tab" aria-selected={activeTab === 'year'} tabIndex="-1"> Year</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="card-body">
        <div className="tab-content" id="myTabContent">
          {(
            <div className="tab-pane fade show active" id="day" role="tabpanel">

              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={data} barCategoryGap="50%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill={primaryColor} barSize={50} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}